import React from "react";
import { motion, AnimatePresence } from "framer-motion";

interface PenaltyAnimationProps {
  active: boolean;
  type: "draw2" | "draw4" | "missedUno";
  cardsCount: number;
  prefersReducedMotion?: boolean;
}

/**
 * Dramatic penalty animation for Draw +2, Draw +4, and Missed UNO
 * Shows visual feedback with scale + fade + shake effects
 */
export const PenaltyAnimation: React.FC<PenaltyAnimationProps> = ({
  active,
  type,
  cardsCount,
  prefersReducedMotion = false,
}) => {
  // Get penalty animation type from theme
  const getPenaltyAnimationType = (): string => {
    if (typeof window === "undefined") return "pulse";
    const root = document.documentElement;
    const animation = getComputedStyle(root).getPropertyValue("--penalty-animation");
    return animation ? animation.trim() : "pulse";
  };

  const getPenaltyConfig = () => {
    // Use CSS variables for colors
    const draw2Color = typeof window !== "undefined" 
      ? getComputedStyle(document.documentElement).getPropertyValue("--penalty-draw2") || "#4a90e2"
      : "#4a90e2";
    const draw4Color = typeof window !== "undefined"
      ? getComputedStyle(document.documentElement).getPropertyValue("--penalty-draw4") || "#ff6b35"
      : "#ff6b35";
    const errorColor = typeof window !== "undefined"
      ? getComputedStyle(document.documentElement).getPropertyValue("--card-red") || "#e74c3c"
      : "#e74c3c";

    switch (type) {
      case "draw2":
        return {
          title: "Draw +2",
          emoji: "📥",
          color: draw2Color,
          bgGradient: `linear-gradient(135deg, ${draw2Color} 0%, color-mix(in srgb, ${draw2Color} 80%, black) 100%)`,
        };
      case "draw4":
        return {
          title: "Draw +4",
          emoji: "📥",
          color: draw4Color,
          bgGradient: `linear-gradient(135deg, ${draw4Color} 0%, color-mix(in srgb, ${draw4Color} 80%, black) 100%)`,
        };
      case "missedUno":
        return {
          title: "Failed to call UNO",
          emoji: "❌",
          color: errorColor,
          bgGradient: `linear-gradient(135deg, ${errorColor} 0%, color-mix(in srgb, ${errorColor} 80%, black) 100%)`,
        };
    }
  };

  const config = getPenaltyConfig();

  return (
    <AnimatePresence>
      {active && (
        <>
          {/* Red flash vignette - brief flash for missed UNO, longer for draw penalties */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: type === "missedUno" 
                ? (prefersReducedMotion ? [0, 0.2, 0] : [0, 0.3, 0]) // Brief flash for UNO missed
                : [0, 0.4, 0], // Longer flash for draw penalties
            }}
            exit={{ opacity: 0 }}
            transition={{
              duration: type === "missedUno" 
                ? (prefersReducedMotion ? 0.15 : 0.2) // Faster for UNO missed
                : 0.3,
              times: type === "missedUno" ? [0, 0.5, 1] : [0, 0.15, 1],
            }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(ellipse at center, rgba(231, 76, 60, ${type === "missedUno" ? "0.3" : "0.4"}) 0%, transparent 70%)`,
              zIndex: 6000,
              pointerEvents: "none",
            }}
          />

          {/* Penalty message - minimal for missed UNO, full for draw penalties */}
          {type === "missedUno" ? (
            // ANIMATION: Minimal visual feedback for missed UNO (no text-heavy modal)
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: [0, 1, 0],
                scale: prefersReducedMotion ? [0.9, 1, 0.9] : [0.9, 1.05, 0.9],
              }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{
                duration: prefersReducedMotion ? 0.2 : 0.3,
                times: [0, 0.5, 1],
              }}
              style={{
                position: "fixed",
                top: "20%",
                left: "50%",
                transform: "translateX(-50%)",
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "rgba(231, 76, 60, 0.9)",
                zIndex: 6001,
                boxShadow: "0 0 30px rgba(231, 76, 60, 0.8)",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "32px",
              }}
            >
              ❌
            </motion.div>
          ) : (
            // Full penalty message for draw +2/+4 - uses theme-based animation
            <motion.div
              className="penalty-effect"
              data-animation={getPenaltyAnimationType()}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{
                opacity: [0, 1, 1, 0],
                scale: prefersReducedMotion ? [0.8, 1, 1, 0.9] : [0.8, 1.1, 1, 0.9],
                y: [20, 0, 0, -10],
                x: prefersReducedMotion ? 0 : [0, -8, 8, -8, 8, 0],
              }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                duration: prefersReducedMotion ? 0.2 : 0.3,
                times: [0, 0.2, 0.8, 1],
                x: prefersReducedMotion ? undefined : {
                  duration: 0.3,
                  times: [0, 0.2, 0.4, 0.6, 0.8, 1],
                },
              }}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                padding: "24px 40px",
                background: config.bgGradient,
                backdropFilter: "blur(10px)",
                color: "white",
                borderRadius: "20px",
                zIndex: 6001,
                boxShadow: `0 12px 60px ${config.color}80, 0 0 40px ${config.color}40`,
                pointerEvents: "none",
                textAlign: "center",
                border: "4px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              <motion.div
                animate={{
                  scale: prefersReducedMotion ? [1, 1.05, 1] : [1, 1.1, 1],
                }}
                transition={{
                  duration: prefersReducedMotion ? 0.15 : 0.2,
                  repeat: 1,
                }}
                style={{
                  fontSize: "32px",
                  marginBottom: "8px",
                }}
              >
                {config.emoji}
              </motion.div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "900",
                  marginBottom: "4px",
                  textShadow: "0 2px 10px rgba(0,0,0,0.5)",
                }}
              >
                {config.title}
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  opacity: 0.9,
                }}
              >
                {cardsCount} cards
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};
